import type { WorkspaceCapability } from '../app/contracts/domain';

/**
 * WI-26 — THE ONE PLACE the data panel decides what it may offer.
 *
 * ═══ WHAT THIS REPLACES ═══════════════════════════════════════════════════════════════════════
 *
 * `WorkspaceCards.tsx` computed its own answer:
 *
 *     const isDisabled = !props.plan || props.plan.status !== 'approved'
 *                        || !!props.isLocked || !!props.dataPipelineInflight;
 *
 * Three unrelated facts in one expression, and the first of them was a `plan.status` PROXY for a
 * server rule it mirrored no part of. The server enforces
 * `WorkspaceImportGate.EvaluateForSourceDataUpload` over a twelve-state ingest set plus two archive
 * axes; the panel asked whether the latest plan row said `approved`. The two disagree in both
 * directions:
 *
 *   • The server ACCEPTS an upload at `data_upload` whose latest plan version is a feedback-created
 *     draft — the loop-back flow — and the panel disabled it. (`WorkspacePage`'s
 *     `POST_APPROVAL_STATES` exists to compensate for exactly this lag elsewhere.)
 *   • The server REFUSES an archived workspace, and a workspace whose PARENT MODEL is archived, on
 *     any state at all. The panel knew about neither.
 *
 * ═══ THE RULE IS THE SERVER'S ════════════════════════════════════════════════════════════════
 *
 * ⛔ Nothing here re-derives eligibility. `capability` is the server's own verdict, projected onto
 * `GET /api/workspaces/{id}` by `WorkspaceCapabilityProjection` from the SAME public entry point
 * the three `/files/source-data` arms enforce with. A second FE spelling of the rule is precisely
 * the defect this module exists to delete, and this file's own history is the precedent: four of
 * the five hand-written copies of the EXPORT-enablement rule lived in `WorkspaceCards.tsx`.
 *
 * The two client facts that are NOT the eligibility rule stay named and separate:
 *   • `dataPipelineInflight` — a workspace-level ingest/delete roundtrip is in flight. Every state
 *     it covers is one the server considers ELIGIBLE; this is a concurrency guard against racing an
 *     in-flight pipeline, not a verdict about the workspace.
 *   • `capabilityUnavailable` — no verdict arrived. Its own outcome, never folded into "refused".
 */

/**
 * Every ineligibility code `EvaluateForSourceDataUpload` can emit, in the server's declaration
 * order.
 *
 * ⚠️ SEVEN (2026-10-21: `workspace_terminal` added — the user's `complete` lock, which this surface
 * used to report as `report_generation_started`, certifier finding D-2/D-4; it is emitted ONLY by
 * `EvaluateForSourceDataUpload`, so `EXPORT_INELIGIBILITY_REASON_CODES` does not carry it). The
 * original sentence follows, kept per the correction convention: SIX, NOT THE SEVEN
 * `EXPORT_INELIGIBILITY_REASON_CODES` carries. Two members of
 * `WorkspaceImportGate.Reasons` are unreachable on THIS surface and are absent deliberately rather
 * than copied across:
 *   • `no_approved_plan` — the upload entry point takes no `hasApprovedPlan` argument at all
 *     (`enforcePlanClause: false`), which is the whole reason it is a separate entry point.
 *   • `unknown_workflow_state` — only `WorkspaceParkGate` can emit it; on this path an unknown
 *     state is refused one clause earlier by the `EligibleForDataIngest` membership check.
 * Listing a code this surface cannot produce would put a sentence in the catalogue that no user can
 * ever be shown, and would quietly widen what the panel claims to understand.
 */
export const DATA_PANEL_REASON_CODES = [
  'archived',
  'model_archived',
  'no_workflow_state',
  'report_generation_started',
  'workspace_terminal',
  'failed_state',
  'workspace_busy',
] as const;

export type DataPanelReasonCode = (typeof DATA_PANEL_REASON_CODES)[number];

export interface DataPanelGates {
  /** May the user add source data right now? The server's verdict AND no in-flight pipeline. */
  canUpload: boolean;
  /**
   * TRUE when no verdict arrived — the workspace read carried no `capabilities` block.
   *
   * ⛔ The panel must render an ERROR on this, never a disabled uploader. "You may not do this" and
   * "we could not find out" are indistinguishable once both are grey, and they mean opposite
   * things: the first is a fact about the workspace, the second is a fault on our side.
   */
  capabilityUnavailable: boolean;
  /** The server's refusal code when it refused, else null. Never render this — localize it. */
  reasonCode: string | null;
  /**
   * F-BUSY-01 — the phase slug that rides WITH the code, and the reason `reasonCode` alone was not
   * enough to render an honest sentence. The vocabulary is deliberately coarse: `workspace_busy` is
   * every non-error state below the report-generation cutoff, which on the upload surface is exactly
   * the pre-plan population (`project_created` / `draft` / `files_uploaded` / `plan_generating` /
   * `plan_ready`) — a brand-new project, where "busy — wait for the current step to finish" is false
   * in both halves. The slug says `before_plan`, which is what the enforcing 409 has always sent and
   * what the-truth.md §3.2 requires the locked panel to say.
   *
   * Null when eligible, and null for a refusal whose state has no phase — the resolver falls back to
   * the code's own line then, exactly as i18next does for the 409.
   */
  reasonContext: string | null;
  /**
   * RULING R-C — the two lock populations, separated. A model archive locks by INHERITANCE and
   * leaves the project's own status untouched, so "this project was archived" and "the model above
   * it was archived" are different facts pointing at different remedies. They look identical to a
   * user and are not.
   */
  lockedByOwnArchive: boolean;
  lockedByParentModel: boolean;
  /** A workspace-level ingest/delete roundtrip is in flight — a concurrency guard, not a verdict. */
  pipelineBusy: boolean;
}

/**
 * Decide, from the server's projected verdict plus the client's own in-flight facts.
 *
 * @param capability the projected `capabilities.sourceDataUpload`. `undefined`/`null` means NO
 *   VERDICT ARRIVED — see `capabilityUnavailable`. It is never treated as a refusal.
 */
export function deriveDataPanelGates(input: {
  capability?: WorkspaceCapability | null;
  dataPipelineInflight?: boolean;
}): DataPanelGates {
  const { capability, dataPipelineInflight } = input;
  const pipelineBusy = !!dataPipelineInflight;

  // `eligible` must be a real boolean. A block that arrived without it is a contract violation, not
  // a refusal — the same "no verdict" terminal as an absent block, reached a different way.
  if (typeof capability?.eligible !== 'boolean') {
    return {
      canUpload: false,
      capabilityUnavailable: true,
      reasonCode: null,
      reasonContext: null,
      lockedByOwnArchive: false,
      lockedByParentModel: false,
      pipelineBusy,
    };
  }

  const reasonCode = capability.eligible ? null : (capability.reasonCode ?? null);

  return {
    canUpload: capability.eligible && !pipelineBusy,
    capabilityUnavailable: false,
    reasonCode,
    // Carried only alongside a refusal, for the same reason the server only computes it there: a
    // slug without a code selects nothing, and a slug on an ALLOW would be a phase answer about a
    // workspace that just passed the gate.
    reasonContext: reasonCode === null ? null : (capability.context ?? null),
    lockedByOwnArchive: reasonCode === 'archived',
    lockedByParentModel: reasonCode === 'model_archived',
    pipelineBusy,
  };
}

/**
 * Is the data pop-up chain (the metadata review dialog) read-only right now?
 *
 * Operator ruling 2026-09-25 (auditing) extended to development and validation by the ruling of
 * 2026-10-19 (ADO #185, the-truth.md §3.4a amendment (j)): a settled report no longer locks it, for
 * any service type. Upload and removal after a report go through the same pop-up flow, so only work
 * in progress (a section in flight, a report generating) or the user's own lock (`complete`) makes
 * it read-only. The server enforces the same: the upload gate admits `section_finalizing`, a live
 * generation or regeneration refuses with a typed 409, and `complete` stays refused.
 *
 * A user-stopped section persists as `generation_failed` (not an in-flight status), so it does not
 * keep the chain locked.
 */
export function isDataPopupLocked(input: {
  anySectionInFlight: boolean;
  isGeneratingReport: boolean;
  workflowState: string | null | undefined;
}): boolean {
  return input.anySectionInFlight || input.isGeneratingReport || input.workflowState === 'complete';
}
